/**
 * Automation Triggers Configuration
 *
 * Centralized configuration for all automation triggers.
 * Triggers define WHEN an automation fires (e.g., client signup, purchase, visit completed).
 *
 * Each trigger has:
 * - `label`: Display text (e.g., "signed up", "failed a payment")
 * - `delayable`: Whether timing can be configured
 * - `timingConfig`: Allowed delay types and before/after options
 * - `Component`: Self-contained React component for edit/readonly UI
 *
 * Architecture:
 * - Import TRIGGERS for static access to trigger configs
 * - Each trigger's Component handles both edit and readonly modes via `readOnly` prop
 * - Shared building blocks: TimingSection, TriggerSelect, StripePlanFields, etc.
 */

import { FC } from 'react';

import * as TriggerComponents from './triggers';
import { AutomationItem } from './types';

export interface TriggerComponentProps {
  automation: AutomationItem;
  index: number;
  readOnly: boolean;
}

export interface TimingConfig {
  delayTypes?: ('day' | 'week' | 'month' | 'hour' | 'minute')[];
  allowBefore?: boolean;
}

export interface TriggerConfig {
  /** Display label for the trigger (e.g., "signed up", "failed a payment") */
  label: string;
  /** Whether this trigger supports delays */
  delayable: boolean;
  /** Allowed delay types (if delayable) */
  delayTypes?: ('day' | 'week' | 'month' | 'hour' | 'minute')[];
  /** Whether delay is shown BEFORE trigger (e.g., "X days before trial ends") */
  beforeTrigger?: boolean;
  /** Whether this trigger requires a service selection */
  requiresService?: boolean;
  /** Whether this trigger requires a plan selection */
  requiresPlan?: boolean;
  /** Whether this trigger requires an assessment selection */
  requiresAssessment?: boolean;
  /** Whether this trigger requires a count input (e.g., "completed X visits") */
  requiresCount?: boolean;
  /** Whether this trigger requires tag selection */
  requiresTags?: boolean;
  /** Whether this trigger requires group selection */
  requiresGroup?: boolean;
  /**
   * Whether this trigger requires sellable type selection.
   * When true, shows a dropdown for selecting: plan, group, product, package
   * Used by 'any' and 'some' triggers for purchase-based automations
   */
  requiresSellableType?: boolean;
  /**
   * Whether this trigger requires sellable item selection.
   * When true (and requiresSellableType is also true), shows a multi-select
   * for picking specific items after the type is selected.
   * Used by 'some' trigger for "purchased specific [items]"
   */
  requiresSellableItems?: boolean;
  /**
   * Whether this trigger hides the "Who?" section.
   * Used for triggers that send to staff instead of clients (e.g., low-stock-threshold)
   */
  hideWhoSection?: boolean;
  /** Optional group for categorized dropdowns */
  group?: string;
  /** Timing configuration for this trigger */
  timingConfig?: TimingConfig;
  /** Component that renders this trigger's configuration UI */
  Component: FC<TriggerComponentProps>;
}

export const TRIGGERS: Record<string, TriggerConfig> = {
  // ==========================================================================
  // Purchase Triggers
  // In v2, the stored reminderType is 'all' for "purchased anything"
  // The 'any' vs 'some' is a UI concept based on whether reminderTypeId has items
  // ==========================================================================
  all: {
    label: 'purchases anything',
    delayable: true,
    group: 'Purchases',
    timingConfig: { delayTypes: ['minute', 'hour', 'day', 'week', 'month'], allowBefore: false },
    Component: TriggerComponents.PurchasedAnyTrigger,
  },
  // Alias for 'all' - some v2 code uses 'any' as a trigger option ID
  any: {
    label: 'purchased any',
    delayable: true,
    requiresSellableType: true, // Shows sellable type dropdown (any plan, any group, etc.)
    group: 'Purchases',
    timingConfig: { delayTypes: ['minute', 'hour', 'day', 'week', 'month'], allowBefore: false },
    Component: TriggerComponents.PurchasedAnyTrigger,
  },
  some: {
    label: 'purchased specific',
    delayable: true,
    requiresSellableType: true, // Shows sellable type dropdown + item multi-select
    requiresSellableItems: true, // Shows item multi-select after type is selected
    group: 'Purchases',
    timingConfig: { delayTypes: ['minute', 'hour', 'day', 'week', 'month'], allowBefore: false },
    Component: TriggerComponents.PurchasedSpecificTrigger,
  },
  // Sellable type triggers (when user selects a specific type)
  workout_plan: {
    label: 'purchases plan(s)',
    delayable: true,
    group: 'Purchases',
    timingConfig: { delayTypes: ['minute', 'hour', 'day', 'week', 'month'], allowBefore: false },
    Component: TriggerComponents.PurchasedAnyTrigger,
  },
  group: {
    label: 'purchases group(s)',
    delayable: true,
    group: 'Purchases',
    timingConfig: { delayTypes: ['minute', 'hour', 'day', 'week', 'month'], allowBefore: false },
    Component: TriggerComponents.PurchasedAnyTrigger,
  },
  product: {
    label: 'purchases product(s)',
    delayable: true,
    group: 'Purchases',
    timingConfig: { delayTypes: ['minute', 'hour', 'day', 'week', 'month'], allowBefore: false },
    Component: TriggerComponents.PurchasedAnyTrigger,
  },
  package: {
    label: 'purchases package(s)',
    delayable: true,
    group: 'Purchases',
    timingConfig: { delayTypes: ['minute', 'hour', 'day', 'week', 'month'], allowBefore: false },
    Component: TriggerComponents.PurchasedAnyTrigger,
  },
  client: {
    label: 'purchases client training',
    delayable: true,
    group: 'Purchases',
    timingConfig: { delayTypes: ['minute', 'hour', 'day', 'week', 'month'], allowBefore: false },
    Component: TriggerComponents.PurchasedAnyTrigger,
  },

  // ==========================================================================
  // Lifecycle triggers
  // ==========================================================================
  'client-signup': {
    label: 'signed up',
    delayable: true,
    timingConfig: { delayTypes: ['minute', 'hour', 'day', 'week', 'month'], allowBefore: false },
    Component: TriggerComponents.ClientSignupTrigger,
  },
  'opened-app': {
    label: 'opened the app',
    delayable: false,
    requiresCount: true,
    Component: TriggerComponents.OpenedAppTrigger,
  },
  'app-open-interval': {
    label: 'opened the app (interval)',
    delayable: false,
    Component: TriggerComponents.AppOpenIntervalTrigger,
  },
  birthdate: {
    label: 'birthday',
    delayable: false,
    Component: TriggerComponents.BirthdateTrigger,
  },
  anniversary: {
    label: 'membership anniversary',
    delayable: false,
    Component: TriggerComponents.AnniversaryTrigger,
  },
  group_joined: {
    label: 'been added to this group',
    delayable: true,
    delayTypes: ['day'],
    requiresGroup: true,
    group: 'User Actions',
    timingConfig: { delayTypes: ['day'], allowBefore: false },
    Component: TriggerComponents.GroupJoinedTrigger,
  },

  // ==========================================================================
  // Payment triggers
  // ==========================================================================
  'successful-payment': {
    label: 'had a successful payment',
    delayable: false,
    Component: TriggerComponents.SuccessfulPaymentTrigger,
  },
  'failed-payment': {
    label: 'failed a payment',
    delayable: true,
    delayTypes: ['hour'],
    timingConfig: { delayTypes: ['hour'], allowBefore: false },
    Component: TriggerComponents.FailedPaymentTrigger,
  },
  'trial-end': {
    label: 'a trial ending',
    delayable: true,
    beforeTrigger: true,
    timingConfig: { delayTypes: ['day', 'week', 'month'], allowBefore: true },
    Component: TriggerComponents.TrialEndTrigger,
  },
  'subscription-renewal': {
    label: 'a subscription renewal',
    delayable: true,
    beforeTrigger: true,
    delayTypes: ['day', 'week', 'month'],
    timingConfig: { delayTypes: ['day', 'week', 'month'], allowBefore: true },
    Component: TriggerComponents.SubscriptionRenewalTrigger,
  },
  'subscription-downgrade': {
    label: 'cancelled a subscription',
    delayable: false,
    group: 'User Actions',
    Component: TriggerComponents.SubscriptionDowngradeTrigger,
  },
  'package-expired': {
    label: 'an expired package',
    delayable: true,
    beforeTrigger: true,
    delayTypes: ['day', 'week', 'month'],
    timingConfig: { delayTypes: ['day', 'week', 'month'], allowBefore: true },
    Component: TriggerComponents.PackageExpiredTrigger,
  },

  // ==========================================================================
  // Visit triggers
  // ==========================================================================
  'visit-completed': {
    label: 'checked in/completed a visit',
    delayable: true,
    requiresService: true,
    requiresCount: true,
    group: 'Visits',
    timingConfig: { delayTypes: ['minute', 'hour', 'day', 'week', 'month'], allowBefore: false },
    Component: TriggerComponents.VisitCompletedTrigger,
  },
  'visit-booked': {
    label: 'booked a visit',
    delayable: false,
    requiresService: true,
    group: 'Visits',
    Component: TriggerComponents.VisitBookedTrigger,
  },
  'book-appointment': {
    label: 'booked a visit',
    delayable: false,
    requiresService: true,
    group: 'Visits',
    Component: TriggerComponents.BookAppointmentTrigger,
  },
  'visit-closed': {
    label: 'closed out a visit',
    delayable: false,
    requiresService: true,
    group: 'Visits',
    Component: TriggerComponents.VisitClosedTrigger,
  },
  'visit-no-showed': {
    label: 'no showed a visit',
    delayable: false,
    requiresService: true,
    group: 'Visits',
    Component: TriggerComponents.VisitNoShowTrigger,
  },
  'visit-late-canceled': {
    label: 'late canceled a visit',
    delayable: false,
    requiresService: true,
    group: 'Visits',
    Component: TriggerComponents.VisitLateCanceledTrigger,
  },
  'no-visit-closed': {
    label: 'has not closed out a visit in',
    delayable: false,
    group: 'Visits',
    Component: TriggerComponents.NoVisitClosedTrigger,
  },

  // ==========================================================================
  // Plan triggers
  // ==========================================================================
  'plan-started': {
    label: 'started a plan',
    delayable: false,
    requiresPlan: true,
    Component: TriggerComponents.PlanStartedTrigger,
  },
  'start-plan': {
    label: 'started a plan',
    delayable: false,
    requiresPlan: true,
    Component: TriggerComponents.StartPlanTrigger,
  },
  'workout-plan-completed': {
    label: 'completed a plan',
    delayable: false,
    requiresPlan: true,
    group: 'User Actions',
    Component: TriggerComponents.WorkoutPlanCompletedTrigger,
  },
  'plan-workout-completed': {
    label: 'completed a plan workout',
    delayable: false,
    requiresPlan: true,
    Component: TriggerComponents.PlanWorkoutCompletedTrigger,
  },
  'plan-workouts-completed': {
    label: 'completed plan workouts',
    delayable: false,
    requiresPlan: true,
    requiresCount: true,
    Component: TriggerComponents.PlanWorkoutsCompletedTrigger,
  },
  'workout-completed': {
    label: 'completed workouts',
    delayable: false,
    requiresCount: true,
    Component: TriggerComponents.WorkoutCompletedTrigger,
  },
  'week-plan-ending': {
    label: 'week plan ending soon',
    delayable: true,
    beforeTrigger: true,
    delayTypes: ['day'],
    timingConfig: { delayTypes: ['day'], allowBefore: true },
    Component: TriggerComponents.WeekPlanEndingTrigger,
  },
  'calendar-plan-ending': {
    label: 'calendar plan ending soon',
    delayable: true,
    beforeTrigger: true,
    delayTypes: ['day'],
    timingConfig: { delayTypes: ['day'], allowBefore: true },
    Component: TriggerComponents.CalendarPlanEndingTrigger,
  },
  'calendar-ending': {
    label: 'calendar ending soon',
    delayable: true,
    beforeTrigger: true,
    delayTypes: ['day'],
    timingConfig: { delayTypes: ['day'], allowBefore: true },
    Component: TriggerComponents.CalendarEndingTrigger,
  },

  // ==========================================================================
  // Assessment triggers
  // ==========================================================================
  assessment: {
    label: 'completed this assessment',
    delayable: true,
    delayTypes: ['hour', 'minute'],
    requiresAssessment: true,
    group: 'User Actions',
    timingConfig: { delayTypes: ['hour', 'minute'], allowBefore: false },
    Component: TriggerComponents.AssessmentTrigger,
  },
  'assessment-completed': {
    label: 'completed an assessment',
    delayable: true,
    delayTypes: ['hour', 'minute'],
    requiresAssessment: true,
    timingConfig: { delayTypes: ['hour', 'minute'], allowBefore: false },
    Component: TriggerComponents.AssessmentCompletedTrigger,
  },

  // ==========================================================================
  // Tag triggers
  // ==========================================================================
  'add-tag': {
    label: 'tag added',
    delayable: false,
    requiresTags: true,
    Component: TriggerComponents.TagAddedTrigger,
  },
  'remove-tag': {
    label: 'tag removed',
    delayable: false,
    requiresTags: true,
    Component: TriggerComponents.TagRemovedTrigger,
  },

  // ==========================================================================
  // Scheduled/recurring triggers
  // ==========================================================================
  workout: {
    label: 'scheduled workout',
    delayable: true,
    timingConfig: { delayTypes: ['day', 'hour', 'minute'], allowBefore: false },
    Component: TriggerComponents.WorkoutTrigger,
  },
  'upcoming-appointment': {
    label: 'upcoming appointment',
    delayable: true,
    timingConfig: { delayTypes: ['day', 'hour', 'minute'], allowBefore: false },
    Component: TriggerComponents.UpcomingAppointmentTrigger,
  },
  'visits-left': {
    label: 'visits left threshold',
    delayable: false,
    requiresCount: true,
    Component: TriggerComponents.VisitsLeftTrigger,
  },
  'low-stock-threshold': {
    label: 'low stock threshold',
    delayable: false,
    requiresCount: true,
    hideWhoSection: true, // Sends to staff, not clients
    Component: TriggerComponents.LowStockThresholdTrigger,
  },

  // ==========================================================================
  // Cancel User triggers
  // These are specific to the Cancel User action
  // ==========================================================================
  cancel_subscription: {
    label: 'a subscription has cancelled',
    delayable: false,
    Component: TriggerComponents.CancelSubscriptionTrigger,
  },
  expired_package: {
    label: 'a package has expired',
    delayable: false,
    Component: TriggerComponents.ExpiredPackageTrigger,
  },
};

// =============================================================================
// Trigger Option Type (for per-action trigger lists)
// =============================================================================

export interface TriggerOption {
  value: string;
  label: string;
  group?: string;
}

/**
 * Convert trigger IDs to options for select dropdown
 */
export const getTriggerOptionsFromIds = (
  triggerIds: string[]
): TriggerOption[] => {
  const options: TriggerOption[] = [];
  for (const id of triggerIds) {
    const config = TRIGGERS[id];
    if (config) {
      options.push({
        value: id,
        label: config.label,
        group: config.group,
      });
    }
  }
  return options;
};

/**
 * Get the default delay types for all triggers
 */
export const DEFAULT_DELAY_TYPES = [
  { value: 'minute', label: 'minute(s)' },
  { value: 'hour', label: 'hour(s)' },
  { value: 'day', label: 'day(s)' },
  { value: 'week', label: 'week(s)' },
  { value: 'month', label: 'month(s)' },
];

/**
 * Get delay type options for a specific trigger
 */
export const getDelayTypesForTrigger = (
  triggerId: string | null | undefined
): TriggerOption[] => {
  if (!triggerId) return DEFAULT_DELAY_TYPES;

  const config = TRIGGERS[triggerId];
  if (!config || !config.delayable) return [];

  if (config.delayTypes) {
    return config.delayTypes.map((type) => ({
      value: type,
      label: `${type}(s)`,
    }));
  }

  return DEFAULT_DELAY_TYPES;
};

/**
 * Check if a trigger is delayable
 */
export const isTriggerDelayable = (
  triggerId: string | null | undefined
): boolean => {
  if (!triggerId) return true;
  const config = TRIGGERS[triggerId];
  return config?.delayable ?? true;
};

/**
 * Check if a trigger supports a specific delay type
 */
export const triggerSupportsDelayType = (
  triggerId: string | null | undefined,
  delayType: 'day' | 'week' | 'month' | 'hour' | 'minute'
): boolean => {
  if (!triggerId) return true; // Default - support all types if no trigger
  const config = TRIGGERS[triggerId];
  if (!config || !config.delayable) return false;

  // Check timingConfig.delayTypes first (preferred), then delayTypes
  const allowedTypes = config.timingConfig?.delayTypes || config.delayTypes;
  if (allowedTypes) {
    return allowedTypes.includes(delayType);
  }

  // By default, support all types if delayable with no specific types defined
  return true;
};

/**
 * Check if a trigger shows delay "before" (e.g., "X days before trial ends")
 */
export const isTriggerBeforeType = (
  triggerId: string | null | undefined
): boolean => {
  if (!triggerId) return false;
  const config = TRIGGERS[triggerId];
  return config?.beforeTrigger ?? false;
};

/**
 * Check if a trigger requires sellable type selection.
 * When true, the UI should show a dropdown for selecting: plan, group, product, package
 */
export const triggerRequiresSellableType = (
  triggerId: string | null | undefined
): boolean => {
  if (!triggerId) return false;
  const config = TRIGGERS[triggerId];
  return config?.requiresSellableType ?? false;
};

/**
 * Check if a trigger requires sellable item selection (multi-select).
 * When true (and sellable type is selected), show the items multi-select.
 */
export const triggerRequiresSellableItems = (
  triggerId: string | null | undefined
): boolean => {
  if (!triggerId) return false;
  const config = TRIGGERS[triggerId];
  return config?.requiresSellableItems ?? false;
};

/**
 * Sellable type options for purchase triggers.
 * These are the types of items that can be purchased.
 */
export const SELLABLE_TYPE_OPTIONS = [
  { value: 'workout_plan', label: 'Plan(s)' },
  { value: 'group', label: 'Group(s)' },
  { value: 'product', label: 'Product(s)' },
  { value: 'package', label: 'Package(s)' },
] as const;
